import { track, hasAnalyticsConsent } from './analytics';
import { apiClient } from '../api/client';
import { getCachedFingerprint } from './fingerprint';
import { getUtmParams } from './utm';
import type { UtmParams } from './utm';

const VISIT_KEY = 'wds_visited';

/**
 * Track an event — fires to GA4 and stores in the DB.
 * Safe to call for anonymous users (no auth required).
 * UTM params are automatically merged into properties if present.
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
  sessionId?: string,
): void {
  const utm = getUtmParams();
  const enriched = Object.keys(utm).length
    ? { ...utm, ...properties }
    : properties;

  if (!hasAnalyticsConsent()) return;

  // GA4
  track(event, enriched);

  // Backend (fire and forget — never throw)
  apiClient
    .post('/api/v1/track', { event, properties: enriched, session_id: sessionId })
    .catch(() => {});
}

/**
 * Track a site visit once per browser session (not per route change).
 * Fires to the backend unconditionally (internal analytics, not advertising).
 * Also registers the anon session so device/country data is captured.
 */
export function trackSiteVisit(): void {
  if (sessionStorage.getItem(VISIT_KEY)) return;
  sessionStorage.setItem(VISIT_KEY, '1');

  const sessionId = getCreateSessionId();
  const utm: UtmParams = getUtmParams();

  // Register anon session with UTM + landing page (first-touch attribution)
  apiClient.post('/api/v1/sessions', {
    fingerprint: getCachedFingerprint(),
    session_id: sessionId,
    landing_page: window.location.pathname + window.location.search,
    ...utm,
  }).catch(() => {});

  // Store visit event in DB (no consent gate — internal analytics only)
  apiClient.post('/api/v1/track', {
    event: 'site_visit',
    properties: { path: window.location.pathname, ...utm },
    session_id: sessionId,
  }).catch(() => {});
}

/**
 * Get or create a stable anonymous session ID for /create users.
 * Stored in sessionStorage so it resets each browser session.
 */
export function getCreateSessionId(): string {
  const key = 'wedsy_create_session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    sessionStorage.setItem(key, id);
  }
  return id;
}
